/**
 * Centralized axios HTTP client + GLOBAL interceptors.
 *
 * Why: every component was building its own axios calls scattered across the
 * codebase. That made it hard to:
 *   - Send credentials consistently (we rely on cookies for auth)
 *   - Handle 401 (auto-redirect to /login)
 *   - Handle 5xx (show a friendly toast instead of a silent failure)
 *   - Centralize the API base URL
 *
 * Two things happen at module import time:
 *   1. We register response interceptors on the GLOBAL `axios` so every
 *      existing component benefits without code changes (just import the
 *      module once at app boot).
 *   2. We export a pre-configured `api` instance with `baseURL` set to
 *      `${REACT_APP_BACKEND_URL}/api` for new code that wants the cleaner
 *      `api.get("/billing/me")` ergonomics.
 *
 * NOTE: existing code that imports raw `axios` still works — this module is
 * additive.
 */
import axios from "axios";
import { toast } from "sonner";

// API base — runtime-detected from the current page's origin (NOT the
// build-time `REACT_APP_BACKEND_URL` env). See `App.js` for the rationale:
// same-origin deployment via Emergent's ingress means `window.location.origin`
// always points at the right backend in every environment.
const BACKEND_URL = (typeof window !== "undefined" ? window.location.origin : "");
export const API_BASE = `${BACKEND_URL}/api`;

// ── Session token storage (cookie + localStorage fallback) ───────────────
// Why: in some production proxy setups (Cloudflare with certain caching/edge
// rules) Set-Cookie headers can be stripped before reaching the browser,
// or browsers can reject SameSite cookies. The fallback path is to send the
// session token as `Authorization: Bearer <token>` from localStorage.
//
// At login, both the cookie AND the URL fragment are populated. This module
// reads any token from the URL fragment (#token=xyz) on import, persists it
// to localStorage, then attaches it to outgoing requests as a Bearer header.
// If the cookie path works, the Bearer header is redundant but harmless;
// if cookies are stripped, Bearer keeps auth flowing.
const TOKEN_KEY = "wokelo_session_token";

function captureTokenFromUrl() {
  if (typeof window === "undefined") return;
  const hash = window.location.hash || "";
  if (hash.includes("token=")) {
    const m = hash.match(/[#&]token=([^&]+)/);
    if (m) {
      try {
        localStorage.setItem(TOKEN_KEY, decodeURIComponent(m[1]));
      } catch (_) { /* private mode / quota */ }
      // Strip the token from the URL so it isn't logged or shared.
      const cleanedHash = hash.replace(/[#&]?token=[^&]+/, "");
      window.history.replaceState(null, "", window.location.pathname + window.location.search + cleanedHash);
    }
  }
}
captureTokenFromUrl();

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (_) { return null; }
}

export function clearStoredToken() {
  try { localStorage.removeItem(TOKEN_KEY); } catch (_) { /* */ }
}

// ── Pre-configured instance for new code ─────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 60_000,
});

function attachBearer(config) {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
}
axios.interceptors.request.use(attachBearer);
api.interceptors.request.use(attachBearer);

// ── Shared response handler used by BOTH the global axios and our `api` instance
//
// Behaviour:
//   - Network failures (no response)  → SILENT for GETs; toast only on
//                                       mutations when ECONNABORTED.
//                                       Globally toasting on cancellations,
//                                       CORS preflight blips during page
//                                       load, and tab-switch cancellations
//                                       was noisy and not actionable.
//   - Timeout (ECONNABORTED)          → toast ONLY on user-initiated
//                                       mutations (POST/PUT/PATCH/DELETE).
//                                       Background GET polls stay silent.
//   - 401 Unauthorized                → silent (component decides whether to
//                                       redirect; some pages rely on 401 to
//                                       detect anonymous users)
//   - 402 Payment Required            → silent (UI shows upgrade modal)
//   - 4xx with `detail`               → silent (caller shows toast / form error)
//   - 5xx                             → toast ONLY on user-initiated
//                                       mutations (POST/PUT/PATCH/DELETE)
//                                       by default. GETs stay silent
//                                       because they're typically polls
//                                       and a single blip resolves on
//                                       the next interval anyway. Callers
//                                       can flip either default:
//                                         { _suppressToast: true }    — silence even mutations
//                                         { _showToastOnError: true } — surface GET 5xx errors
//
// We never swallow the error — the caller's `catch` still fires.
// Cold-start retry: deployments take ~30-60s for the backend pod to be
// fully ready behind the K8s ingress (and Cloudflare). During that
// window, requests come back as 502/503/504 from K8s ingress OR
// 520/521/522/523/524/525/526/527/530 from Cloudflare when the origin
// returns malformed/empty responses. Without retry, the very first
// user who hits the site after deploy sees a "Something went wrong"
// toast and may bounce.
//
// Strategy: 6 in-process retries with exponential backoff (1s, 2s, 4s,
// 8s, 16s, 32s → ~63s max) for IDEMPOTENT methods (GET/HEAD/OPTIONS)
// AND for explicitly safe-to-retry auth POSTs (login, register,
// forgot-password, verify-email, etc.). These auth POSTs
// are state-machine idempotent — the backend safely handles duplicate
// requests (duplicate login is just a fresh session, duplicate register
// returns "email already exists") so retrying them is risk-free and
// dramatically improves cold-start UX.
//
// Other POST/PUT/DELETE (generate, regen, charge, settings updates)
// are NOT retried because they may have user-visible side effects.
//
// Caller can opt out via config: { _noRetry: true } if they have their
// own retry logic (e.g. polling endpoints).
//
// Tuning history:
//   2026-05-11: Bumped from 3 retries / 7s → 6 retries / 63s for K8s cold-start.
//   2026-07-XX: Added auth-POST retry + Cloudflare 5xx codes after users
//   saw Cloudflare 520 "origin web server sent a response that Cloudflare
//   could not parse" toasts post-deploy. The origin briefly returns
//   malformed responses while the FastAPI app is still warming its
//   connection pool — by the 2nd attempt 1s later the response is clean.

// Cloudflare-specific 5xx codes that ALWAYS indicate "origin not ready
// yet" rather than a genuine bug in our code. Treat the same as
// 502/503/504 for retry purposes. See cloudflare.com/learning/cdn/glossary/.
const CLOUDFLARE_RETRY_STATUSES = new Set([520, 521, 522, 523, 524, 525, 526, 527, 530]);
const K8S_INGRESS_RETRY_STATUSES = new Set([502, 503, 504]);

// Endpoints we KNOW are safe to retry on POST even though POSTs are
// normally non-idempotent. These are the user's first-touch auth flow
// where a transient 5xx blocks them from even getting INTO the app.
// All of them are server-side idempotent:
//   - /auth/login    → duplicate just issues a fresh session token
//   - /auth/register → 400s on duplicate email; never double-creates
//   - /auth/forgot-password → sends an email; the email service
//                              dedupes per recipient within a short window
//   - /auth/verify-email     → idempotent: token consumed on first success
//   - /auth/google/exchange  → OAuth code is single-use; backend handles dup
//   - /auth/logout           → idempotent (clears the session either way)
// (/auth/me is GET so it already retries via the idempotent path; we
// don't need it in this list, but adding it would be a no-op anyway.)
const SAFE_RETRY_POST_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/forgot-password",
  "/auth/verify-email",
  "/auth/google",          // matches /auth/google/* prefix
  "/auth/logout",
];

function urlMatchesSafePost(url) {
  if (!url) return false;
  // Normalise: strip query string, ignore base host so we match on path.
  // The full url can be either absolute (`${API_BASE}/auth/login`) or
  // already a path; both should match the same way.
  const pathOnly = url.split("?")[0];
  return SAFE_RETRY_POST_PATHS.some(p => pathOnly.includes(p));
}

async function maybeRetryColdStart(error) {
  const config = error.config || {};
  const status = error.response?.status;
  const isInfraError =
    K8S_INGRESS_RETRY_STATUSES.has(status) ||
    CLOUDFLARE_RETRY_STATUSES.has(status);
  const method = (config.method || "get").toLowerCase();
  const isIdempotent = method === "get" || method === "head" || method === "options";
  const isSafePost = method === "post" && urlMatchesSafePost(config.url);
  config._retryCount = config._retryCount || 0;
  if (!isInfraError || !(isIdempotent || isSafePost) || config._noRetry || config._retryCount >= 6) {
    return null;
  }
  // Backoff: 1s, 2s, 4s, 8s, 16s, 32s → ~63s max total per request.
  // Comfortably wider than typical Emergent cold-start (30–60s).
  const wait = 1000 * Math.pow(2, config._retryCount);
  config._retryCount += 1;
  await new Promise((r) => setTimeout(r, wait));
  const inst = config._sourceInstance === "api" ? api : axios;
  return inst.request(config);
}

// Network-level retry: when there's no response object at all (ECONNRESET,
// CORS preflight failure during cold-start, TLS handshake blip), the
// global interceptor used to swallow it silently. For the same safe
// endpoints above we want to retry — it almost always succeeds on the
// next attempt once the pod is warm.
async function maybeRetryNetworkError(error) {
  const config = error.config || {};
  const method = (config.method || "get").toLowerCase();
  const isIdempotent = method === "get" || method === "head" || method === "options";
  const isSafePost = method === "post" && urlMatchesSafePost(config.url);
  config._retryCount = config._retryCount || 0;
  if (!(isIdempotent || isSafePost) || config._noRetry || config._retryCount >= 6) {
    return null;
  }
  const wait = 1000 * Math.pow(2, config._retryCount);
  config._retryCount += 1;
  await new Promise((r) => setTimeout(r, wait));
  const inst = config._sourceInstance === "api" ? api : axios;
  return inst.request(config);
}

function onResponseError(error) {
  if (!error.response) {
    // Network-level failure — try the silent retry path FIRST. If we
    // succeed within the budget the user never sees a toast.
    return maybeRetryNetworkError(error).then((retryResult) => {
      if (retryResult) return retryResult;
      // Retries exhausted (or method not eligible). Only surface a
      // toast for genuine timeouts ON USER-INITIATED MUTATIONS. GETs
      // are background data fetches — users don't care about transient
      // timeouts there, and toasting them just spawns noise during
      // long-running deck-status polls.
      const method = (error.config?.method || "get").toLowerCase();
      const isMutation = method === "post" || method === "put" || method === "patch" || method === "delete";
      if (error.code === "ECONNABORTED" && isMutation && !error.config?._suppressToast) {
        toast.error("Request timed out. Please try again.", {
          id: "request-timeout",
          duration: 4000,
        });
      }
      return Promise.reject(error);
    });
  }
  const status = error.response.status;
  const detail = error.response.data?.detail;

  // Try cold-start retry FIRST. If it succeeds we never fire the toast.
  // Only AFTER retries are exhausted do we surface the 5xx error.
  return maybeRetryColdStart(error).then((retryResult) => {
    if (retryResult) return retryResult;

    // ── Toast policy (rewritten 2026-06-29) ──────────────────────────
    // Users complained about the "Reconnecting to the server…" toast
    // popping up during long deck generations. The culprit was the
    // many polling endpoints (deck status every 4s, slide status every
    // 3s, onboarding status, referral notifications, /me/usage…) that
    // hit a single 5xx blip after retries exhausted and fired the
    // toast even though the next poll 4s later would succeed.
    //
    // The right mental model: TOASTS BELONG ON USER-INITIATED ACTIONS,
    // not on background data fetches. So:
    //   • Mutations (POST/PUT/PATCH/DELETE) → toast on 5xx by default.
    //     These are things the user clicked a button to do; they need
    //     immediate feedback if it failed.
    //   • GET (and HEAD/OPTIONS) → DO NOT toast on 5xx by default.
    //     These are polls and background refreshes; the silent retry
    //     net + the natural re-poll loop will recover invisibly. If a
    //     caller specifically wants the toast (e.g., a one-shot user-
    //     triggered fetch), opt-in with `_showToastOnError: true`.
    //   • Existing `_suppressToast: true` opt-out still wins for any
    //     mutation that wants to handle its own messaging (LoginPage).
    const method = (error.config?.method || "get").toLowerCase();
    const isMutation = method === "post" || method === "put" || method === "patch" || method === "delete";
    const explicitOptIn = error.config?._showToastOnError === true;
    const explicitOptOut = error.config?._suppressToast === true;
    const shouldToast = status >= 500
      && !explicitOptOut
      && (explicitOptIn || isMutation);

    if (shouldToast) {
      // De-duplicate with a stable Sonner id. After a deploy, multiple
      // parallel requests can ALL fail at once — without an id the
      // user sees N stacked toasts; with it, sonner replaces in place.
      const isInfra =
        K8S_INGRESS_RETRY_STATUSES.has(status) ||
        CLOUDFLARE_RETRY_STATUSES.has(status);
      const toastId = isInfra ? "infra-5xx" : "server-error-generic";
      const message = isInfra
        ? "Reconnecting to the server… please give it a moment."
        : (typeof detail === "string" ? detail : "Something went wrong on our end. We're looking into it.");
      toast.error(message, { id: toastId, duration: 4000 });
    }
    return Promise.reject(error);
  });
}

// Tag requests with their source instance so the retry helper knows which
// interceptor to bypass on the re-issue (otherwise we'd recurse).
axios.interceptors.request.use((config) => {
  config._sourceInstance = config._sourceInstance || "axios";
  return config;
});
api.interceptors.request.use((config) => {
  config._sourceInstance = "api";
  return config;
});

// Register on BOTH:
//  (a) the global axios import — covers all legacy code (components doing
//      `import axios from "axios"`).
//  (b) the new `api` instance — covers new code using this module's default
//      export.
axios.interceptors.response.use((r) => r, onResponseError);
api.interceptors.response.use((r) => r, onResponseError);

export default api;
